import type { ManualTask } from '@compass/client/types/manual_task';
import { EmptyState } from '@components/EmptyState';
import { GenericTable } from '@components/GenericTable';
import { QuickFilters } from '@components/QuickFilters';
import { useStore } from '@store/index';
import { ListFilter, XCircle } from 'lucide-react';
import React, { useMemo, useState } from 'react';

import { ErrorContainer } from '@/components/ErrorContainer';
import { FormattedDateRelative } from '@/components/FormattedDateRelative';
import { Breadcrumb, BreadcrumbPortal } from '@/components/Header';
import { Badge } from '@/components/ui/badge';
import { DateRangePicker } from '@/components/ui/date-range-picker';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import type { FiltersConfig } from '@/config/quickFilters.config';
import { PAGE_SIZE_OPTIONS } from '@/constants/pagination';
import { useAllMyTasks } from '@/hooks/useAllMyTasks';
import type { TableConfig } from '@/types/table';
import { dateToNaiveString, naiveDateToUtc, naiveStringToDate } from '@/utils/date';
import {
  isPendingTask,
  TASK_FILTER_STATUSES,
  taskStatusBadgeVariant,
  taskStatusLabel,
  taskTypeLabel,
} from '@/utils/manualTasks';
import { buildRoutePath } from '@/utils/routes';

const DEFAULT_PAGE_SIZE = 50;

// Same tab model as My Cases: Pending (non-terminal tasks) is a strict subset of All.
const TAB_ALL = 'all';
const TAB_PENDING = 'pending';
const ANY_STATUS = 'any';

const MY_TASKS_FILTERS: FiltersConfig = {
  filters: [
    {
      id: TAB_PENDING,
      name: 'Pending',
      badgeColor: 'bg-amber-100 text-amber-700',
      filter: {},
    },
    {
      id: TAB_ALL,
      name: 'All',
      badgeColor: 'bg-purple-100 text-purple-700',
      filter: {},
    },
  ],
};

// Default last 7 days as naive (YYYY-MM-DD) strings.
const getDefaultDateRange = () => {
  const today = new Date();
  const past = new Date();
  past.setDate(today.getDate() - 7);
  return { from: dateToNaiveString(past), to: dateToNaiveString(today) };
};

const MyTasksContainer: React.FC = () => {
  const selectedOrganizationId = useStore(state => state.selectedOrganizationId);
  const timeZone = useStore(state => state.getTimeZone)();

  const [activeQuickFilter, setActiveQuickFilter] = useState<string>(TAB_PENDING);
  const [statusFilter, setStatusFilter] = useState<string>(ANY_STATUS);
  const defaultRange = useMemo(() => getDefaultDateRange(), []);
  const [dateFrom, setDateFrom] = useState<string>(defaultRange.from);
  const [dateTo, setDateTo] = useState<string>(defaultRange.to);
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState<number>(DEFAULT_PAGE_SIZE);

  const isPendingTab = activeQuickFilter === TAB_PENDING;

  // The Pending tab only offers non-terminal statuses; switching to it drops a
  // terminal status selection that would otherwise match nothing.
  const statusOptions = isPendingTab
    ? TASK_FILTER_STATUSES.filter(s => isPendingTask(s))
    : TASK_FILTER_STATUSES;
  const handleTabChange = (tab: string) => {
    setActiveQuickFilter(tab);
    if (
      tab === TAB_PENDING &&
      statusFilter !== ANY_STATUS &&
      !isPendingTask(Number(statusFilter))
    ) {
      setStatusFilter(ANY_STATUS);
    }
  };

  React.useEffect(() => {
    setCurrentPage(1);
  }, [dateFrom, dateTo, pageSize, activeQuickFilter, statusFilter]);

  const { isLoading, error, tasks } = useAllMyTasks({
    created_after: dateFrom ? naiveDateToUtc(dateFrom, timeZone, 'start') : undefined,
    created_before: dateTo ? naiveDateToUtc(dateTo, timeZone, 'end') : undefined,
  });

  const pendingTasks = useMemo(() => tasks.filter(t => isPendingTask(t.status)), [tasks]);

  const tabCounts = useMemo(
    () => ({ All: tasks.length, Pending: pendingTasks.length }),
    [tasks.length, pendingTasks.length],
  );

  const filteredTasks = useMemo(() => {
    const inTab = isPendingTab ? pendingTasks : tasks;
    if (statusFilter === ANY_STATUS) return inTab;
    const status = Number(statusFilter);
    return inTab.filter(t => t.status === status);
  }, [isPendingTab, pendingTasks, tasks, statusFilter]);

  const displayedTasks = useMemo(() => {
    const start = (currentPage - 1) * pageSize;
    return filteredTasks.slice(start, start + pageSize);
  }, [filteredTasks, currentPage, pageSize]);

  const hasNextPage = currentPage * pageSize < filteredTasks.length;

  const isDefaultDateRange = dateFrom === defaultRange.from && dateTo === defaultRange.to;
  const hasActiveFilters = !isDefaultDateRange || isPendingTab || statusFilter !== ANY_STATUS;

  const tableConfig = useMemo(
    (): TableConfig<ManualTask> => ({
      columns: [
        {
          key: 'id',
          header: 'Task ID',
          accessor: item => <span className="font-mono text-xs">{item.id}</span>,
          copyable: true,
          copyValue: item => item.id || '',
        },
        {
          key: 'case_record_id',
          header: 'Case ID',
          accessor: item => <span className="font-mono text-xs">{item.case_record_id}</span>,
          copyable: true,
          copyValue: item => item.case_record_id || '',
        },
        {
          key: 'type',
          header: 'Task type',
          accessor: item => taskTypeLabel(item.type),
        },
        {
          key: 'created_at',
          header: 'Created at',
          accessor: item => <FormattedDateRelative value={item.created_at} />,
        },
        {
          key: 'completed_at',
          header: 'Completed at',
          accessor: item =>
            item.completed_at ? (
              <FormattedDateRelative value={item.completed_at} />
            ) : (
              <span className="text-muted-foreground">N/A</span>
            ),
        },
        {
          key: 'status',
          header: 'Status',
          accessor: item => (
            <Badge variant={taskStatusBadgeVariant(item.status)}>
              {taskStatusLabel(item.status)}
            </Badge>
          ),
        },
      ],
      // Tasks span programs, so each row links with its own org/program.
      getRowLink: item => {
        const orgId = item.organization_id || selectedOrganizationId || '';
        if (!orgId || !item.program_id || !item.case_record_id) return '#';
        return buildRoutePath(`/cases/${item.case_record_id}`, orgId, item.program_id);
      },
      getRowKey: item => item.id || '',
      emptyState: hasActiveFilters
        ? {
            title: isPendingTab ? 'No pending tasks' : 'No tasks match your filters',
            description: isPendingTab
              ? 'Every task assigned to you in this date range is completed or cancelled.'
              : 'Try adjusting the date range or status.',
          }
        : {
            title: 'You have no tasks assigned to you',
            description: 'Tasks assigned to you across this organisation will appear here.',
          },
    }),
    [selectedOrganizationId, hasActiveFilters, isPendingTab],
  );

  const breadcrumb = (
    <BreadcrumbPortal>
      <Breadcrumb>
        <Breadcrumb.Item>My Tasks</Breadcrumb.Item>
      </Breadcrumb>
    </BreadcrumbPortal>
  );

  if (!selectedOrganizationId) {
    return (
      <>
        {breadcrumb}
        <EmptyState
          title="No organization selected"
          description="Select an organization to view your tasks."
          icon={XCircle}
          iconClassName="text-muted-foreground"
        />
      </>
    );
  }

  if (error) {
    return (
      <>
        {breadcrumb}
        <ErrorContainer errorMessage={error?.message || 'Failed to load tasks'} />
      </>
    );
  }

  return (
    <>
      {breadcrumb}
      <div className="flex flex-col h-full min-h-0">
        <div className="flex flex-col flex-1 min-h-0 px-6">
          <div className="flex items-center gap-3 mt-4">
            <Select value={statusFilter} onValueChange={setStatusFilter}>
              <SelectTrigger id="my-tasks-status" className="w-44 gap-2" aria-label="Status">
                {/* Leading icon at the same weight as the date range picker's beside it. */}
                <ListFilter className="h-4 w-4 opacity-70 shrink-0" />
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value={ANY_STATUS}>All statuses</SelectItem>
                {statusOptions.map(s => (
                  <SelectItem key={s} value={String(s)}>
                    {taskStatusLabel(s)}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            <DateRangePicker
              initialDateFrom={dateFrom ? naiveStringToDate(dateFrom) : undefined}
              initialDateTo={dateTo ? naiveStringToDate(dateTo) : undefined}
              timezone={timeZone}
              onUpdate={({ range }) => {
                if (!range.from) return;
                setDateFrom(dateToNaiveString(range.from));
                setDateTo(dateToNaiveString(range.to ?? range.from));
              }}
              showCompare={false}
            />
          </div>

          <div className="mt-4">
            <QuickFilters
              activeFilter={activeQuickFilter}
              onFilterChange={handleTabChange}
              filtersConfig={MY_TASKS_FILTERS}
              counts={tabCounts}
              countsLoading={isLoading}
            />
          </div>

          <GenericTable
            config={tableConfig}
            data={displayedTasks}
            loading={isLoading}
            error={null}
            currentPage={currentPage}
            hasNextPage={hasNextPage}
            onPageChange={setCurrentPage}
            pageSize={pageSize}
            pageSizeOptions={[...PAGE_SIZE_OPTIONS]}
            onPageSizeChange={setPageSize}
            selectionMode={false}
          />
        </div>
      </div>
    </>
  );
};

export default MyTasksContainer;
